import type {IGalleryImage} from "@pg-mono/gallery";
import {compact} from "@pg-mono/nodash";

import {IOfferMainImage} from "../types/property/IOfferMainImage";
import {IPropertyListItemImageThumbnails} from "../types/property/IPropertyListItemImageThumbnails";
import {IPropertyPlanImages} from "../types/property/IPropertyPlanImages";

export interface IKmGalleryImage extends IGalleryImage {
    isPlan?: boolean;
}

export interface IPropertyGalleryImagesOffer {
    images: {
        thumbnails: IPropertyListItemImageThumbnails;
    }[];
    main_image?: IOfferMainImage;
}

/** Minimal property shape for gallery image mapping (detail DTO, property box, etc.). */
export interface IPropertyGalleryImagesSource {
    external_plan_url?: string | null;
    images: {
        thumbnails: IPropertyListItemImageThumbnails;
    }[];
    plan_images?: IPropertyPlanImages | null;
    offer: IPropertyGalleryImagesOffer;
}

type IPropertyGallerySourceImage = IPropertyGalleryImagesSource["images"][number];
type IOfferGallerySourceImage = IPropertyGalleryImagesOffer["images"][number];

const toGalleryImageFromExternalPlanUrl = (externalPlanUrl: string): IKmGalleryImage => ({
    srcSm: externalPlanUrl,
    srcMd: externalPlanUrl,
    srcLg: externalPlanUrl,
    isPlan: true
});

const toGalleryImageFromPlanImages = (planImages: IPropertyPlanImages): IKmGalleryImage => {
    const primaryImage = planImages.p_img_980 || planImages.p_img_560 || planImages.p_img_500 || planImages.p_img_140x80;
    const srcSm = planImages.p_img_500 || planImages.p_img_140x80 || planImages.p_img_560 || planImages.p_img_980;

    return {
        srcSm,
        srcMd: primaryImage,
        srcLg: planImages.p_img_1500 || planImages.p_img_764x430 || primaryImage,
        isPlan: true
    };
};

const toGalleryImageFromPropertyImage = (image: IPropertyGallerySourceImage): IKmGalleryImage => {
    const {thumbnails} = image;
    const primaryImage = thumbnails.g_img_750 || thumbnails.p_img_560 || thumbnails.g_img_500;

    return {
        srcSm: thumbnails.g_img_500 || thumbnails.p_img_560 || thumbnails.g_img_750,
        srcMd: primaryImage,
        srcLg: thumbnails.g_img_1500 || thumbnails.g_img_1280 || thumbnails.g_img_800 || primaryImage
    };
};

const toGalleryImageFromOfferImage = (image: IOfferGallerySourceImage): IKmGalleryImage | null => {
    const {thumbnails} = image;
    const primaryImage = thumbnails.g_img_750 || thumbnails.g_img_500;

    if (primaryImage == null) return null;

    return {
        srcSm: thumbnails.g_img_500 || thumbnails.g_img_750,
        srcMd: primaryImage,
        srcLg: thumbnails.g_img_1500 || thumbnails.g_img_1280 || thumbnails.g_img_800 || primaryImage
    };
};

const toGalleryImageFromMainImage = (mainImage: IOfferMainImage): IKmGalleryImage | null => {
    const srcSm = mainImage.m_img_500 || mainImage.m_img_224x147 || mainImage.m_img_155x87;
    const srcMd = mainImage.m_img_750 || mainImage.m_img_500 || mainImage.m_img_224x147 || srcSm;
    const srcLg = mainImage.m_img_1500 || mainImage.m_img_750 || srcMd;
    const galleryImage = {srcSm, srcMd, srcLg};

    return hasRenderableGalleryImage(galleryImage) ? galleryImage : null;
};

const hasRenderableGalleryImage = (image: IKmGalleryImage): boolean => Boolean(image.srcSm || image.srcMd || image.srcLg);

const toRenderablePropertyGalleryImages = (image: IPropertyGallerySourceImage): IKmGalleryImage[] => {
    const galleryImage = toGalleryImageFromPropertyImage(image);

    return hasRenderableGalleryImage(galleryImage) ? [galleryImage] : [];
};

const toRenderableOfferGalleryImages = (image: IOfferGallerySourceImage): IKmGalleryImage[] => {
    const galleryImage = toGalleryImageFromOfferImage(image);

    return galleryImage ? [galleryImage] : [];
};

export const getGalleryImageDisplayUrl = (image: IKmGalleryImage): string | null => image.srcMd || image.srcSm || image.srcLg || null;

export const getGalleryViewerImageUrl = (image: IKmGalleryImage): string | null => image.srcLg || image.srcMd || image.srcSm || null;

const getPropertyGalleryMainImage = (property: IPropertyGalleryImagesSource): IKmGalleryImage | null => {
    if (!property.offer.main_image) return null;

    return toGalleryImageFromMainImage(property.offer.main_image);
};

const getPropertyGalleryPhotoImages = (property: IPropertyGalleryImagesSource): IKmGalleryImage[] => [
    ...property.images.flatMap(toRenderablePropertyGalleryImages),
    ...property.offer.images.flatMap(toRenderableOfferGalleryImages)
];

const getPropertyGalleryExternalPlanImage = (property: IPropertyGalleryImagesSource): IKmGalleryImage | null => {
    if (!property.external_plan_url) return null;

    return toGalleryImageFromExternalPlanUrl(property.external_plan_url);
};

const getPropertyGalleryPlanImage = (property: IPropertyGalleryImagesSource): IKmGalleryImage | null => {
    if (!property.plan_images) return null;

    const planImage = toGalleryImageFromPlanImages(property.plan_images);

    return hasRenderableGalleryImage(planImage) ? planImage : null;
};

/** Order: main_image → plans → photos. Without main_image, plans first. */
export const getPropertyGalleryImages = (property: IPropertyGalleryImagesSource): IKmGalleryImage[] => {
    const externalPlanImage = getPropertyGalleryExternalPlanImage(property);
    const planImage = getPropertyGalleryPlanImage(property);
    const mainImage = getPropertyGalleryMainImage(property);
    const photoImages = getPropertyGalleryPhotoImages(property);

    return compact([mainImage, externalPlanImage, planImage, ...photoImages]);
};

export const getPropertyGalleryImageUrlsFromImages = (images: IKmGalleryImage[]): string[] => {
    return compact(images.map(getGalleryImageDisplayUrl));
};

/** Inline slider / property box URLs in the same order as {@link getPropertyGalleryImages}. */
export const getPropertyGalleryImageUrls = (property: IPropertyGalleryImagesSource): string[] => {
    return getPropertyGalleryImageUrlsFromImages(getPropertyGalleryImages(property));
};
import type {IGalleryImage} from "@pg-mono/gallery";
import {compact} from "@pg-mono/nodash";

import {IOfferMainImage} from "../types/property/IOfferMainImage";
import {IPropertyListItemImageThumbnails} from "../types/property/IPropertyListItemImageThumbnails";
import {IPropertyPlanImages} from "../types/property/IPropertyPlanImages";

export interface IKmGalleryImage extends IGalleryImage {
    isPlan?: boolean;
}

export interface IPropertyGalleryImagesOffer {
    images: {
        thumbnails: IPropertyListItemImageThumbnails;
    }[];
    main_image?: IOfferMainImage;
}

/** Minimal property shape for gallery image mapping (detail DTO, property box, etc.). */
export interface IPropertyGalleryImagesSource {
    external_plan_url?: string | null;
    images: {
        thumbnails: IPropertyListItemImageThumbnails;
    }[];
    plan_images?: IPropertyPlanImages | null;
    offer: IPropertyGalleryImagesOffer;
}

type IPropertyGallerySourceImage = IPropertyGalleryImagesSource["images"][number];
type IOfferGallerySourceImage = IPropertyGalleryImagesOffer["images"][number];

const toGalleryImageFromExternalPlanUrl = (externalPlanUrl: string): IKmGalleryImage => ({
    srcSm: externalPlanUrl,
    srcMd: externalPlanUrl,
    srcLg: externalPlanUrl,
    isPlan: true
});

const toGalleryImageFromPlanImages = (planImages: IPropertyPlanImages): IKmGalleryImage => {
    const primaryImage = planImages.p_img_980 || planImages.p_img_560 || planImages.p_img_500 || planImages.p_img_140x80;
    const srcSm = planImages.p_img_500 || planImages.p_img_140x80 || planImages.p_img_560 || planImages.p_img_980;

    return {
        srcSm,
        srcMd: primaryImage,
        srcLg: planImages.p_img_1500 || planImages.p_img_764x430 || primaryImage,
        isPlan: true
    };
};

const toGalleryImageFromPropertyImage = (image: IPropertyGallerySourceImage): IKmGalleryImage => {
    const {thumbnails} = image;
    const primaryImage = thumbnails.g_img_750 || thumbnails.p_img_560 || thumbnails.g_img_500;

    return {
        srcSm: thumbnails.g_img_500 || thumbnails.p_img_560 || thumbnails.g_img_750,
        srcMd: primaryImage,
        srcLg: thumbnails.g_img_1500 || thumbnails.g_img_1280 || thumbnails.g_img_800 || primaryImage
    };
};

const toGalleryImageFromOfferImage = (image: IOfferGallerySourceImage): IKmGalleryImage | null => {
    const {thumbnails} = image;
    const primaryImage = thumbnails.g_img_750 || thumbnails.g_img_500;

    if (primaryImage == null) return null;

    return {
        srcSm: thumbnails.g_img_500 || thumbnails.g_img_750,
        srcMd: primaryImage,
        srcLg: thumbnails.g_img_1500 || thumbnails.g_img_1280 || thumbnails.g_img_800 || primaryImage
    };
};

const toGalleryImageFromMainImage = (mainImage: IOfferMainImage): IKmGalleryImage | null => {
    const srcSm = mainImage.m_img_500 || mainImage.m_img_224x147 || mainImage.m_img_155x87;
    const srcMd = mainImage.m_img_750 || mainImage.m_img_500 || mainImage.m_img_224x147 || srcSm;
    const srcLg = mainImage.m_img_1500 || mainImage.m_img_750 || srcMd;
    const galleryImage = {srcSm, srcMd, srcLg};

    return hasRenderableGalleryImage(galleryImage) ? galleryImage : null;
};

const hasRenderableGalleryImage = (image: IKmGalleryImage): boolean => Boolean(image.srcSm || image.srcMd || image.srcLg);

const toRenderablePropertyGalleryImages = (image: IPropertyGallerySourceImage): IKmGalleryImage[] => {
    const galleryImage = toGalleryImageFromPropertyImage(image);

    return hasRenderableGalleryImage(galleryImage) ? [galleryImage] : [];
};

const toRenderableOfferGalleryImages = (image: IOfferGallerySourceImage): IKmGalleryImage[] => {
    const galleryImage = toGalleryImageFromOfferImage(image);

    return galleryImage ? [galleryImage] : [];
};

export const getGalleryImageDisplayUrl = (image: IKmGalleryImage): string | null => image.srcMd || image.srcSm || image.srcLg || null;

export const getGalleryViewerImageUrl = (image: IKmGalleryImage): string | null => image.srcLg || image.srcMd || image.srcSm || null;

const getPropertyGalleryMainImage = (property: IPropertyGalleryImagesSource): IKmGalleryImage | null => {
    if (!property.offer.main_image) return null;

    return toGalleryImageFromMainImage(property.offer.main_image);
};

const getPropertyGalleryPhotoImages = (property: IPropertyGalleryImagesSource): IKmGalleryImage[] => [
    ...property.images.flatMap(toRenderablePropertyGalleryImages),
    ...property.offer.images.flatMap(toRenderableOfferGalleryImages)
];

const getPropertyGalleryExternalPlanImage = (property: IPropertyGalleryImagesSource): IKmGalleryImage | null => {
    if (!property.external_plan_url) return null;

    return toGalleryImageFromExternalPlanUrl(property.external_plan_url);
};

const getPropertyGalleryPlanImage = (property: IPropertyGalleryImagesSource): IKmGalleryImage | null => {
    if (!property.plan_images) return null;

    const planImage = toGalleryImageFromPlanImages(property.plan_images);

    return hasRenderableGalleryImage(planImage) ? planImage : null;
};

/** Order: main_image → plans → photos. Without main_image, plans first. */
export const getPropertyGalleryImages = (property: IPropertyGalleryImagesSource): IKmGalleryImage[] => {
    const externalPlanImage = getPropertyGalleryExternalPlanImage(property);
    const planImage = getPropertyGalleryPlanImage(property);
    const mainImage = getPropertyGalleryMainImage(property);
    const photoImages = getPropertyGalleryPhotoImages(property);

    return compact([mainImage, externalPlanImage, planImage, ...photoImages]);
};

export const getPropertyGalleryImageUrlsFromImages = (images: IKmGalleryImage[]): string[] => {
    return compact(images.map(getGalleryImageDisplayUrl));
};

/** Inline slider / property box URLs in the same order as {@link getPropertyGalleryImages}. */
export const getPropertyGalleryImageUrls = (property: IPropertyGalleryImagesSource): string[] => {
    return getPropertyGalleryImageUrlsFromImages(getPropertyGalleryImages(property));
};
